/** 账户界面页面(v1.0.1 风格左侧导航,独立于面板)。 */

import { useCallback, useEffect, useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { Bot as BotIcon, Radio, Puzzle, Clock, Loader2, KeyRound } from 'lucide-react';
import {
  fetchAccountSummary, enableAccountBot, disableAccountBot, redeemAccountCode,
} from '../api/client';
import type { AccountSummary } from '../api/types';
import { Button } from '../components/Button';
import { useGuideActive } from '../components/GuideOverlay';
import { StatusBadge } from '../components/StatusBadge';
import { ExpiryBadge } from '../components/ExpiryBadge';
import { RenewDialog } from '../components/AccountDialogs';
import { MarqueeText } from '../components/MarqueeText';
import { useAuth } from '../hooks/useAuth';
import { AccountPasswordForm } from '../components/AccountPasswordForm';
import { showToast } from '../hooks/useToast';
import {
  OverviewTab, LiveTab, BotTab, TimerTab, PluginsTab, LibraryTab, ExpiryNoticeSwitch,
} from './AccountDetailPage';

// ================================================================== //
// 账户摘要 Hook
// ================================================================== //

export function useAccountSummary(pollMs?: number): { acc: AccountSummary | null; reload: () => void } {
  const auth = useAuth();
  const [acc, setAcc] = useState<AccountSummary | null>(null);

  const load = useCallback(async () => {
    if (!auth.accountId) return;
    try {
      setAcc(await fetchAccountSummary(auth.accountId));
    } catch { /* ignore */ }
  }, [auth.accountId]);

  useEffect(() => {
    load();
    if (!pollMs) return;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  return { acc, reload: load };
}

// ================================================================== //
// 概览页(v1.0.1 仪表盘风格:统计卡片 + 饼图 + Bot 启停开关)
// ================================================================== //

const PIE_COLORS = ['#10b981', '#a1a1aa'];
const PIE_COLORS_BLUE = ['#2563eb', '#a1a1aa'];

export function AccountOverviewPage() {
  const auth = useAuth();
  const { acc, reload } = useAccountSummary(10000);
  const [toggling, setToggling] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  if (!acc) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const toggleBot = async () => {
    if (!auth.accountId) return;
    setToggling(true);
    try {
      if (acc.bot_enabled) {
        await disableAccountBot(auth.accountId);
        showToast('success', 'Bot 已停用', '');
      } else {
        await enableAccountBot(auth.accountId);
        showToast('success', 'Bot 已启用', '');
      }
    } catch (e: any) {
      showToast('error', '操作失败', e.message);
    } finally {
      setToggling(false);
    }
  };

  const liveData = [
    { name: '已连接', value: acc.room_connected ? 1 : 0 },
    { name: '未连接', value: acc.room_connected ? 0 : 1 },
  ];
  const pluginData = [
    { name: '已启用', value: acc.enabled_plugin_count },
    { name: '未启用', value: Math.max(0, acc.plugin_count - acc.enabled_plugin_count) },
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      {/* 头部 */}
      {/* 不换行：账户名一长，名字 /「公共 Bot」/「剩余 N 天」就会折成两行。
          改成名字在剩余宽度里自己滚动，其余元素 shrink-0 固定不缩 */}
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold min-w-0 flex-1">
          <MarqueeText text={acc.name} />
        </h1>
        <div className="flex items-center gap-2 shrink-0">
          <span className={
            'rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ' +
            (acc.bot_public
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300')
          }>
            {acc.bot_public ? '公共 Bot' : '私有 Bot'}
          </span>
          <ExpiryBadge expiresAt={acc.expires_at} pausedReason={acc.paused_reason} />
        </div>
        <div className="shrink-0" data-guide="ov-redeem">
          <Button size="sm" variant="secondary" icon={<KeyRound className="w-4 h-4" />}
            onClick={() => setRedeemOpen(true)}>
            兑换授权码
          </Button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* 下面这几个 data-guide 是「操作指引」的定位锚点（见 components/GuideOverlay.tsx）——
            纯标记，不影响样式与行为。改结构时若删掉了带标记的元素，指引那一步会
            退化成「只有文字、没有高亮」，不会报错，所以容易不知不觉漏掉 */}
        <div className="card" data-guide="ov-bot">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <BotIcon className="w-4 h-4" /> Bot 状态
              </div>
              <StatusBadge status={acc.bot_enabled && acc.bot_available ? 'enabled' : 'disabled'}
                label={acc.bot_enabled ? '已启用' : '已停用'} />
            </div>
            <p className="mt-2 font-semibold text-lg truncate">{acc.bot_name || '未配置 Bot'}</p>
            <div className="mt-3" data-guide="ov-bot-toggle">
              <Button size="sm" variant={acc.bot_enabled ? 'secondary' : 'success'}
                loading={toggling} disabled={toggling || !acc.bot_available} onClick={toggleBot}>
                {acc.bot_enabled ? '停用 Bot' : '启用 Bot'}
              </Button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Radio className="w-4 h-4" /> 直播间
              </div>
              <StatusBadge status={acc.room_connected ? 'online' : 'offline'}
                label={acc.room_connected ? '已连接' : '未连接'} />
            </div>
            <p className="mt-2 font-semibold text-lg truncate">
              {acc.room_name || (acc.room_id ? `房间 ${acc.room_id}` : '未绑定')}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {acc.room_id ? '已绑定 1 个直播间' : '尚未绑定直播间'}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Puzzle className="w-4 h-4" /> 插件
            </div>
            <p className="mt-2 font-semibold text-lg">{acc.enabled_plugin_count} 启用</p>
            <p className="text-xs text-gray-400 mt-1">共 {acc.plugin_count} 个插件</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4" /> 定时消息
            </div>
            <p className="mt-2 font-semibold text-lg">{acc.normal_timer_message_count} 条</p>
            <p className="text-xs text-gray-400 mt-1">
              普通消息
              {acc.plugin_timer_message_count > 0
                && ` · 另有 ${acc.plugin_timer_message_count} 条插件消息`}
            </p>
          </div>
        </div>
      </div>

      {/* 饼图 */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="card">
          <div className="card-header"><h3 className="font-semibold">直播间状态</h3></div>
          <div className="card-body h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={liveData} dataKey="value" nameKey="name"
                  innerRadius="55%" outerRadius="80%" paddingAngle={2}>
                  {liveData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="font-semibold">插件状态</h3></div>
          <div className="card-body h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pluginData} dataKey="value" nameKey="name"
                  innerRadius="55%" outerRadius="80%" paddingAngle={2}>
                  {pluginData.map((_, i) => <Cell key={i} fill={PIE_COLORS_BLUE[i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {acc.resume_error && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {acc.resume_error}
        </div>
      )}

      {/* 账户偏好 */}
      <div className="card">
        <div className="card-header"><h3 className="font-semibold">账户偏好</h3></div>
        <div className="card-body flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-gray-700 dark:text-gray-200">开播临期提示</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              开播时若剩余有效期不足 3 天，自动在本直播间发一条续期提醒。默认开启
            </p>
          </div>
          <ExpiryNoticeSwitch acc={acc} />
        </div>
      </div>

      {/* 兑换授权码 */}
      <RenewDialog
        open={redeemOpen}
        accountId={auth.accountId ?? 0}
        accountName={acc.name}
        mode="code"
        loading={redeeming}
        onRenew={async () => {}}
        onRedeem={async (id, code) => {
          setRedeeming(true);
          try {
            await redeemAccountCode(id, code);
            showToast('success', '兑换成功', '');
            setRedeemOpen(false);
            reload();
          } catch (e: any) {
            showToast('error', '兑换失败', e.message);
          } finally { setRedeeming(false); }
        }}
        onCancel={() => setRedeemOpen(false)}
      />
    </div>
  );
}

// ================================================================== //
// 其余页面包装(标题 + 复用面板详情页的 Tab 组件)
// ================================================================== //

function PageShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <h1 className="text-2xl font-bold">{title}</h1>
      {children}
    </div>
  );
}

export function AccountLibraryPage() {
  const { acc } = useAccountSummary();
  const guideActive = useGuideActive();
  if (!acc) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }
  // 指引期间摆固定的演示清单：库可能是空的，「插件库」那一步就没东西可指
  return <LibraryTab acc={acc} demo={guideActive} />;
}

export function AccountLivePage() {
  const { acc } = useAccountSummary(8000);
  const guideActive = useGuideActive();
  if (!acc) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }
  // 没绑直播间 + 指引正浮着 → 进演示态，渲染一个可点的虚拟房间。
  // 少了 guideActive 这个条件，正常使用时也会冒出一个假房间来
  return (
    <PageShell title="直播间">
      <LiveTab acc={acc} demo={guideActive && acc.room_id == null} />
    </PageShell>
  );
}

export function AccountBotPage() {
  const { acc, reload } = useAccountSummary();
  if (!acc) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }
  return <PageShell title="Bot"><BotTab acc={acc} onAccountChanged={reload} /></PageShell>;
}

export function AccountTimerPage() {
  const { acc } = useAccountSummary(30000);
  if (!acc) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }
  return <PageShell title="定时消息"><TimerTab acc={acc} /></PageShell>;
}

export function AccountPluginsPage() {
  const { acc } = useAccountSummary();
  const guideActive = useGuideActive();
  if (!acc) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }
  // 指引期间摆固定的演示清单：账户可能一个插件都没装，那两步就没东西可指
  return <PageShell title="插件"><PluginsTab acc={acc} demo={guideActive} /></PageShell>;
}
export function AccountPasswordPage() {
  const auth = useAuth();
  return (
    <PageShell title="修改密码">
      <div className="card max-w-md">
        <div className="card-body space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            修改后需使用新密码重新登录
          </p>
          {/* 表单与「强制修改默认密码」界面共用，校验规则只此一份 */}
          <AccountPasswordForm
            accountId={auth.accountId ?? 0}
            onDone={() => setTimeout(() => auth.logout(), 1200)}
          />
        </div>
      </div>
    </PageShell>
  );
}
